/**
 * What a BEO can be short of, who is holding it, and what being short costs.
 *
 * This is the file that turns the game from a delivery loop into a political
 * one. Every shortfall names an owning department, and that department's people
 * are somewhere else on the property with their own opening hours — so being
 * two linens short is not a supply problem, it is a question about who you are
 * willing to owe.
 *
 * Three shapes of shortfall, and they cost you in three different currencies:
 *
 *   goods    Banquet storage is simply short. You physically cannot finish.
 *            Costs you the walk, both ways.
 *   staging  A line on the sheet the room cannot meet. Nothing stops you
 *            setting the rest; the grade takes it. Costs you points.
 *   access   Something you use to work is unavailable. Nothing stops you and
 *            nothing is deducted — it just takes longer. Costs you minutes.
 *
 * **Every one of them is somewhere.** `at` names the thing in the blockout the
 * shortfall is talking about, and `where` is that place in words. Resolving a
 * shortfall does not put anything in your hands and does not top up the shelf:
 * it means the department has let go of it, and it is now sitting where its own
 * text has always said it was sitting. You go and get it.
 *
 * That is the whole of why this file has coordinates in it at all. It used to
 * read "Two rounds went out on a rental truck and are sitting on the dock" and
 * then release two rounds into banquet storage two hundred feet away, and the
 * sentence the player was shown had nothing to do with what the game did. Now
 * the fetch is the shortfall: a linen run is forty feet of corridor with three
 * carts to duck behind, and a rounds run is the length of the property down the
 * one straight with nothing on it, with the Banquet Manager walking it.
 *
 * **And where it lands is the one thing standing buys.** `at` is not a place,
 * it is a place per rung — the four in `standing.js`, which are the only thing
 * standing unlocks anywhere. A department that is cold leaves it where it sat.
 * One that is warm walks it half way to the room for you. One that trusts you
 * has it waiting when you arrive, which is no fetch at all.
 *
 * The price does not move. Asking a department that loves you costs the same
 * favour as asking one that does not, and being caught taking from either costs
 * the same standing. What changes is geography, because geography is the thing
 * this property has that a coefficient does not: a rung you can see on the
 * panel turns into a walk you can see out of your own eyes.
 *
 * A rung with no entry falls back down the ladder to the nearest one that has
 * it, so the two access shortfalls — which release nothing and are only ever
 * about the thing that is locked — name one place and are done.
 *
 * The words for a place live in `PLACES`, keyed by the same prop id, so three
 * rungs do not mean three copies of the same sentence. `level.test.js` fails if
 * any rung of any shortfall names something that is not in the world, that
 * there is nowhere to stand at, or that another one has already claimed.
 *
 * `weight` is how often the generator picks it, relative to the others.
 *
 * `minutes` and `fetch` are the board's budgeting estimate and nothing runs a
 * timer on either. They are split because the two halves behave differently:
 * `minutes` is getting the department to let go of it, which costs the same
 * whatever they think of you, and `fetch` is the walk back with it, which is
 * the bit the rung moves. The fetch numbers are the sober, pushed column of
 * `npm run bench`'s third table, rounded — measured rather than guessed, and
 * they should be re-measured whenever the blockout moves.
 *
 * Added together at cold they come to what `minutes` alone used to be, so a
 * board for a player nobody owes anything is the board that was there before.
 * A warm one is lighter by the shorter walk and a trusted one by all of it.
 */

export const SHORTFALL_KINDS = Object.freeze({
  GOODS: 'goods',
  STAGING: 'staging',
  ACCESS: 'access',
});

/**
 * Every place a shortfall can put something, in words.
 *
 * Keyed by the prop it is, because a place is a thing in the blockout and not a
 * string. `spot` is the two or three words a prompt shows; `where` is the line
 * that tells you it has landed.
 *
 * `arrived` is the one that carries a rule: it means this place is somewhere you
 * were going anyway, so there is nothing to fetch. Goods that arrive go onto
 * the shelf and come off it with the rest of the par; staging that arrives is
 * already in the room. Nothing is left in a pile at an `arrived` place and no
 * collection point is built on one, which is also why the three supply shelves
 * can appear here without growing a second prompt.
 */
export const PLACES = {
  // --- where things sit when nobody owes you anything ----------------------
  'hskp-cart-c': {
    spot: 'the end cart',
    where: 'the end housekeeping cart, in the corridor',
  },
  'dock-pallet-a': {
    spot: 'the dock pallet',
    where: 'the dock pallet, at the head of the main run',
  },
  'kitchen-chairs': {
    spot: 'the kitchen pass',
    where: 'the stack behind the kitchen pass',
  },
  'shop-risers': {
    spot: 'the riser stack',
    where: 'the riser stack in the engineering shop',
  },
  'lobby-signboard': {
    spot: 'the front desk',
    where: 'the board at the front desk, on the marble',
  },

  // --- half way, for a department that thinks well enough of you ------------
  // Four of the five are staged against the north wall of the branch, which is
  // what "somebody walked it round for you" looks like on a property with one
  // corridor. Housekeeping go further because they were never far: theirs goes
  // into the room.
  'ballroom-linen-cage': {
    spot: 'the linen cage',
    where: 'a linen cage, wheeled into the room by the service doors',
  },
  'branch-rounds': {
    spot: 'the branch pallet',
    where: 'a pallet in the corridor, half way to the room',
  },
  'branch-chairs': {
    spot: 'the chair stack',
    where: 'a stack of chairs in the corridor, half way to the room',
  },
  'branch-risers': {
    spot: 'the riser dolly',
    where: 'a riser dolly in the corridor, half way to the room',
  },
  'branch-signage': {
    spot: 'the staged board',
    where: 'the board in the corridor, off the marble and half way to the room',
  },

  // --- and where it is when they trust you ---------------------------------
  // No fetch. The goods are on the shelf you were walking to anyway and the
  // staging is in the room before you get there.
  'store-linen': {
    spot: 'the linen shelf',
    where: 'the linen shelf, waiting for you',
    arrived: true,
  },
  'store-rounds': {
    spot: 'the rounds shelf',
    where: 'the rounds shelf, waiting for you',
    arrived: true,
  },
  'store-chairs': {
    spot: 'the chair shelf',
    where: 'the chair shelf, waiting for you',
    arrived: true,
  },
  'stage-deck': {
    spot: 'the stage',
    where: 'set by the stage before you got there',
    arrived: true,
  },
  'col-ne': {
    spot: 'the room entrance',
    where: 'stood at the room entrance before you got there',
    arrived: true,
  },

  // --- and the two that are not deliveries at all ---------------------------
  'cart-banquet': { spot: 'the banquet cart', where: 'the banquet cart, in storage' },
  'ballroom-door-east': {
    spot: 'the near ballroom doors',
    where: 'the near ballroom service doors',
  },
};

/**
 * Where a shortfall's thing is, for a department that feels like this about you.
 *
 * Falls down the ladder rather than requiring every shortfall to fill in every
 * rung: hostile reads cold's entry, and a shortfall that only declares one
 * place has that place whatever anybody thinks of you.
 */
const LADDER = ['hostile', 'cold', 'warm', 'trusted'];

/** The entry for this rung, or the nearest one below it that exists. */
function downTheLadder(map, rung) {
  if (!map) return null;
  for (let i = Math.max(0, LADDER.indexOf(rung)); i >= 0; i -= 1) {
    if (map[LADDER[i]] !== undefined) return map[LADDER[i]];
  }
  return map.cold ?? null;
}

export function placeFor(shortfall, rung = 'cold') {
  const at = shortfall?.at;
  if (!at) return null;
  if (typeof at === 'string') return at;
  return downTheLadder(at, rung);
}

/**
 * What the board should budget for one shortfall, on one rung.
 *
 * The negotiation plus the walk. Hostile and cold come out the same because
 * they *are* the same: there is nowhere worse to leave a thing than where it
 * already was, and nothing else about a department moves with standing. If the
 * bottom of the ladder is ever to cost more than the middle, it will need a
 * rule of its own, and it does not have one.
 */
export function estimateFor(shortfall, rung = 'cold') {
  if (!shortfall) return 0;
  return (shortfall.minutes ?? 0) + (downTheLadder(shortfall.fetch, rung) ?? 0);
}

export const SHORTFALLS = {
  /**
   * The short one. Their carts are parked in the corridor you were walking down
   * anyway, and there are three of them to get behind. This is the shortfall
   * that costs you almost nothing, and it is supposed to be.
   */
  'linen-short': {
    id: 'linen-short',
    kind: SHORTFALL_KINDS.GOODS,
    item: 'linen',
    owner: 'housekeeping',
    label: 'Linen count short',
    /** What you are after, as a sentence says it: "ask Roz for the linen". */
    wants: 'the linen',
    /** Units withheld, as a range. Rolled per event. */
    amount: [2, 4],
    detail: 'Housekeeping has the balance on their carts and has not sent it.',
    at: {
      cold: 'hskp-cart-c',
      // Housekeeping's half way is not half way along the corridor, because the
      // corridor is the whole of their fetch. It is inside the room.
      warm: 'ballroom-linen-cage',
      trusted: 'store-linen',
    },
    weight: 3,
    minutes: 17,
    fetch: { cold: 5, warm: 2, trusted: 0 },
  },
  /**
   * The long one, and the reason the main service run exists.
   *
   * They come off a truck, so they are on the dock, so they are at the head of
   * a hundred and forty-four foot straight with no door in it, no corner and
   * nothing to step behind — and rounds are the heaviest thing in the game, two
   * to a pair of arms. It is the one fetch that is worth a cart and the one
   * fetch where a cart is worth getting on.
   */
  'rounds-short': {
    id: 'rounds-short',
    kind: SHORTFALL_KINDS.GOODS,
    item: 'round',
    owner: 'valet',
    label: 'Rounds short',
    wants: 'the rounds',
    amount: [1, 2],
    detail: 'They came off a rental truck and are still on the dock.',
    at: {
      cold: 'dock-pallet-a',
      warm: 'branch-rounds',
      trusted: 'store-rounds',
    },
    weight: 2,
    minutes: 7,
    fetch: { cold: 13, warm: 7, trusted: 0 },
  },
  /**
   * The middle one, and the only fetch with a choice in it. From the kitchen
   * door the ballroom is about the same distance either way — a hundred and
   * twelve feet east along the branch, or thirty feet east and then the run —
   * so this is where a player who has learned the property picks a route rather
   * than following one.
   */
  'chairs-short': {
    id: 'chairs-short',
    kind: SHORTFALL_KINDS.GOODS,
    item: 'chair',
    owner: 'kitchen',
    label: 'Chairs short',
    wants: 'the chairs',
    amount: [6, 14],
    detail: 'Kitchen took a stack for a staff meal and they are still at the pass.',
    at: {
      cold: 'kitchen-chairs',
      warm: 'branch-chairs',
      trusted: 'store-chairs',
    },
    weight: 3,
    minutes: 15,
    fetch: { cold: 15, warm: 6, trusted: 0 },
  },
  /**
   * Arms only, and it is the shop door that says so.
   *
   * Engineering's door is three feet wide because Engineering hands out
   * permission rather than goods. Now that it hands out staging as well, the
   * door has not changed and neither has the rule: no cart goes through it, so
   * a riser run is the one fetch on the property you make on foot whatever
   * state you are in, and riding does nothing for it at all.
   */
  'risers-missing': {
    id: 'risers-missing',
    kind: SHORTFALL_KINDS.STAGING,
    item: 'riser',
    owner: 'engineering',
    label: 'Risers not delivered',
    wants: 'the risers',
    detail: 'The staging is in the engineering shop behind a work order nobody signed.',
    at: {
      cold: 'shop-risers',
      warm: 'branch-risers',
      // Engineering that trusts you sets them by the stage themselves, which is
      // where risers go, and there is nothing for you to carry.
      trusted: 'stage-deck',
    },
    weight: 2,
    minutes: 16,
    fetch: { cold: 14, warm: 5, trusted: 0 },
  },
  /**
   * The dangerous one, and it is short.
   *
   * The board is at the front desk, the front desk is on the marble, and the
   * marble in a back of house uniform is the first rule the game teaches. Sixty
   * feet of fetch, most of it somewhere you are not allowed to be, carrying
   * something that makes it obvious why you are there.
   */
  'signage-missing': {
    id: 'signage-missing',
    kind: SHORTFALL_KINDS.STAGING,
    item: 'signage',
    owner: 'frontdesk',
    label: 'Lobby signage not set',
    wants: 'the lobby signage',
    detail: 'Front Desk has the board and the letters, and Front Desk is front of house.',
    at: {
      cold: 'lobby-signboard',
      // Warm gets the board off the marble, which is most of what that fetch
      // costs — the distance barely moves and the risk goes to nothing.
      warm: 'branch-signage',
      trusted: 'col-ne',
    },
    weight: 2,
    minutes: 13,
    fetch: { cold: 13, warm: 5, trusted: 0 },
  },
  /*
   * There is no cage anywhere in the blockout and there never was, so the text
   * has been changed rather than the building: the padlock is on the cart, and
   * the cart is where the cart has always been parked.
   */
  'lift-locked': {
    id: 'lift-locked',
    kind: SHORTFALL_KINDS.ACCESS,
    owner: 'engineering',
    label: 'Cart padlocked',
    wants: 'the padlock key',
    detail:
      'The banquet cart is padlocked where it stands in storage and Engineering ' +
      'has the key. Arms only until it is not.',
    // One place, whatever they think of you: a padlock is not a delivery, and
    // there is nowhere else for a cart that is chained where it stands to be.
    at: { cold: 'cart-banquet' },
    weight: 2,
    minutes: 30,
    /** What the access denies. Read by the game, not by the generator. */
    denies: 'cart',
  },
  'dock-blocked': {
    id: 'dock-blocked',
    kind: SHORTFALL_KINDS.ACCESS,
    owner: 'valet',
    label: 'Near ballroom doors chained',
    wants: 'the chain off the ballroom doors',
    detail: 'Valet chained the near doors for a delivery. Use the far ones.',
    at: { cold: 'ballroom-door-east' },
    weight: 2,
    minutes: 20,
    denies: 'barrier:ballroom-door-east',
  },
};

export const SHORTFALL_IDS = Object.keys(SHORTFALLS);

/** Everything a resolved shortfall leaves somewhere for you to go and collect. */
export const COLLECTABLE = Object.values(SHORTFALLS).filter((entry) => entry.item);

/**
 * Every (shortfall, rung) pair that leaves a pile somewhere to be collected.
 *
 * Not the `arrived` ones, which leave nothing: goods that arrive go onto the
 * shelf and staging that arrives is already in the room, so building a
 * collection point on either would be a prompt with nothing behind it — and on
 * the three supply shelves it would be a second prompt on a prop that already
 * has one.
 */
export const PICKUPS = COLLECTABLE.flatMap((shortfall) =>
  [...new Set(Object.values(shortfall.at))]
    .filter((place) => !PLACES[place]?.arrived)
    .map((place) => ({ shortfall, place, ...PLACES[place] }))
);

/** How much spare banquet storage carries beyond what an event calls for. */
export const STOCK_BUFFER = { round: 1, chair: 4, linen: 1 };

export default SHORTFALLS;
